import { Description, Field, Label } from '@headlessui/react';
import classnames from 'classnames';
import React from 'react';

import { useThemeClasses } from '@/lib/legacy-theme/theme-utils';

export interface MultiSelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
  icon?: React.ReactNode;
}

export interface MultiSelectButtonProps {
  label?: string;
  helperText?: string;
  error?: string;
  success?: string;
  variant?: 'default' | 'error' | 'success';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  options: MultiSelectOption[];
  value?: (string | number)[];
  onChange?: (value: (string | number)[]) => void;
  disabled?: boolean;
  required?: boolean;
  maxSelection?: number;
  minSelection?: number;
  layout?: 'horizontal' | 'vertical' | 'grid';
  columns?: number;
}

export const MultiSelectButton: React.FC<MultiSelectButtonProps> = ({
  label,
  helperText,
  error,
  success,
  variant = 'default',
  size = 'md',
  fullWidth = false,
  options,
  value = [],
  onChange,
  disabled,
  required,
  maxSelection,
  minSelection,
  layout = 'horizontal',
  columns = 3,
  ...props
}) => {
  // Base button classes
  const baseButtonClasses =
    'inline-flex items-center justify-center rounded-theme-md border font-theme-medium transition-theme-normal focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

  // Theme-aware variant classes for unselected buttons
  const variantButtonClasses = {
    default: 'border-border bg-surface text-text hover:bg-surface-hover hover:border-primary focus:ring-primary',
    error: 'border-error bg-surface text-text hover:bg-surface-hover hover:border-error focus:ring-error',
    success: 'border-success bg-surface text-text hover:bg-surface-hover hover:border-success focus:ring-success',
  };

  // Theme-aware variant classes for selected buttons
  const selectedButtonClasses = {
    default: 'border-primary bg-primary text-white hover:bg-primary-hover focus:ring-primary',
    error: 'border-error bg-error text-white hover:bg-error-hover focus:ring-error',
    success: 'border-success bg-success text-white hover:bg-success-hover focus:ring-success',
  };

  // Size classes
  const sizeButtonClasses = {
    sm: 'px-theme-sm py-theme-xs text-theme-sm gap-2',
    md: 'px-theme-md py-theme-sm text-theme-base gap-2',
    lg: 'px-theme-lg py-theme-md text-theme-lg gap-3',
  };

  // Icon size classes
  const iconSizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  // Determine the actual variant based on error/success props
  const actualVariant = error ? 'error' : success ? 'success' : variant;

  // Container classes
  const containerClasses = useThemeClasses(
    classnames('flex flex-wrap gap-2', layout === 'vertical' && 'flex-col', layout === 'grid' && `grid grid-cols-${columns} gap-2`, fullWidth && layout !== 'grid' && 'w-full'),
  );

  // Label classes
  const labelClasses = useThemeClasses(classnames('block font-theme-medium text-theme-sm text-text mb-theme-xs', disabled && 'opacity-50'));

  // Helper text classes
  const helperTextClasses = useThemeClasses(classnames('mt-theme-xs text-theme-xs', error ? 'text-error' : success ? 'text-success' : 'text-text-muted'));

  const handleOptionClick = (optionValue: string | number) => {
    if (disabled) return;

    const isSelected = value.includes(optionValue);
    let newValue: (string | number)[];

    if (isSelected) {
      // Remove from selection
      newValue = value.filter((v) => v !== optionValue);
    } else {
      // Add to selection (check max selection limit)
      if (maxSelection && value.length >= maxSelection) {
        return; // Don't add if max selection reached
      }
      newValue = [...value, optionValue];
    }

    onChange?.(newValue);
  };

  const isOptionSelected = (optionValue: string | number) => value.includes(optionValue);
  const isOptionDisabled = (option: MultiSelectOption) => Boolean(disabled || option.disabled || (!isOptionSelected(option.value) && maxSelection && value.length >= maxSelection));

  return (
    <Field>
      {label && (
        <Label className={labelClasses}>
          {label}
          {required && <span className="text-error ml-1">*</span>}
        </Label>
      )}

      <div className={containerClasses} {...props}>
        {options.map((option) => {
          const selected = isOptionSelected(option.value);
          const optionDisabled = isOptionDisabled(option);

          const buttonClasses = useThemeClasses(
            classnames(
              baseButtonClasses,
              selected ? selectedButtonClasses[actualVariant] : variantButtonClasses[actualVariant],
              sizeButtonClasses[size],
              fullWidth && layout === 'horizontal' && 'flex-1',
              optionDisabled && 'opacity-50 cursor-not-allowed',
            ),
          );

          const iconClasses = useThemeClasses(classnames('flex-shrink-0', iconSizeClasses[size]));

          return (
            <button key={option.value} type="button" className={buttonClasses} onClick={() => handleOptionClick(option.value)} disabled={optionDisabled}>
              {option.icon && <span className={iconClasses}>{option.icon}</span>}
              <span className="truncate">{option.label}</span>
            </button>
          );
        })}
      </div>

      {(helperText || error || success) && <Description className={helperTextClasses}>{error || success || helperText}</Description>}
    </Field>
  );
};

MultiSelectButton.displayName = 'MultiSelectButton';

export default MultiSelectButton;
